import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sessionsApi } from '../../api/sessions';
import { namesApi } from '../../api/names';
import './BookSheet.css';

// Follow-up still open:
//   #567 — user-definable Focus session names in New Session + the session list

export function defaultCharacterFocusTitle(handle) {
  const trimmed = (handle || '').trim();
  return trimmed ? `Character: ${trimmed}` : 'Character: unnamed';
}

/**
 * Start Character Focus from the Character tile.
 * A name is never required — unnamed is a valid start.
 */
export default function StartCharacterFocusDialog({
  storyId,
  existingCharacters = [],
  onClose,
}) {
  const navigate = useNavigate();
  const [characterName, setCharacterName] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [sessionTitle, setSessionTitle] = useState(defaultCharacterFocusTitle(''));
  const [titleDirty, setTitleDirty] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [seedNotes, setSeedNotes] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  const applyHandle = (handle, id = null) => {
    setSelectedId(id);
    setCharacterName(handle);
    if (!titleDirty) setSessionTitle(defaultCharacterFocusTitle(handle));
  };

  const handle = characterName.trim();
  const focusTarget = handle || 'unnamed';
  const sessionName = sessionTitle.trim() || defaultCharacterFocusTitle(handle);

  const handleSuggest = async () => {
    const notes = seedNotes.trim();
    if (notes.length < 8) {
      setSuggestError('A sentence or two is enough — age, job, who they stand next to.');
      return;
    }
    setSuggesting(true);
    setSuggestError(null);
    setSuggestions([]);
    try {
      const data = await namesApi.suggest(storyId, {
        description: notes,
        kind: 'character',
        count: 6,
      });
      const hits = data?.suggestions || [];
      setSuggestions(hits);
      if (!hits.length) {
        setSuggestError('Nothing came back — try a slightly fuller note, or skip this and start the session.');
      }
    } catch (e) {
      setSuggestError(e.message || 'Could not suggest names.');
    } finally {
      setSuggesting(false);
    }
  };

  const handleOpen = async () => {
    if (creating) return;
    setCreating(true);
    setError(null);
    try {
      const response = await sessionsApi.create(storyId, {
        name: sessionName,
        description: seedNotes.trim() || null,
        guidance_mode: 'focus',
        template: 'character',
        focus_target_name: focusTarget,
      });
      const session = response?.data || response;
      if (!session?.id) throw new Error('Session was created without an id.');
      onClose();
      navigate(`/chat/${session.id}`);
    } catch (e) {
      setError(e.message || 'Could not open a Character Focus session.');
      setCreating(false);
    }
  };

  return (
    <div className="bs-modal-backdrop" onClick={creating ? undefined : onClose}>
      <div
        className="bs-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Create Character Focus Session"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bs-modal__header">
          <h2>Create Character Focus Session</h2>
          <button className="bs-modal__close" onClick={onClose} disabled={creating} aria-label="Close">&times;</button>
        </div>

        <p className="bs-modal__intro">
          Talk about a character who is not on the page yet — or pick one you already named.
          A name is optional. You can keep going unnamed.
        </p>

        {error && <div className="bs-error">{error}</div>}

        {existingCharacters.length > 0 && (
          <div className="bs-edit__field">
            <span className="bs-edit__label">Existing characters</span>
            <ul className="bs-wizard-list" role="listbox" aria-label="Existing characters">
              {existingCharacters.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className={`bs-wizard-row bs-focus-start__pick ${selectedId === c.id ? 'bs-focus-start__pick--active' : ''}`}
                    onClick={() => applyHandle(c.label === 'no name yet' ? '' : c.label, c.id)}
                  >
                    {c.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="bs-edit__field">
          <label className="bs-edit__label" htmlFor="bs-focus-character-name">Character name</label>
          <input
            id="bs-focus-character-name"
            type="text"
            className="bs-edit__input"
            value={characterName}
            onChange={(e) => applyHandle(e.target.value, null)}
            placeholder="Optional — leave blank to stay unnamed"
          />
        </div>

        <div className="bs-edit__field">
          <label className="bs-edit__label" htmlFor="bs-focus-session-title">Session title</label>
          <input
            id="bs-focus-session-title"
            type="text"
            className="bs-edit__input"
            value={sessionTitle}
            onChange={(e) => {
              setTitleDirty(true);
              setSessionTitle(e.target.value);
            }}
            placeholder={defaultCharacterFocusTitle(handle)}
          />
        </div>

        <div className="bs-edit__field">
          <label className="bs-edit__label" htmlFor="bs-focus-seed-notes">What I know (optional)</label>
          <textarea
            id="bs-focus-seed-notes"
            className="bs-edit__textarea"
            rows={3}
            value={seedNotes}
            onChange={(e) => setSeedNotes(e.target.value)}
            placeholder="Age, job, link to someone already named — skip this if you just want to chat."
          />
          <div className="bs-tile__add-form-actions">
            <button
              type="button"
              className="bs-btn bs-btn--secondary bs-btn--small"
              onClick={handleSuggest}
              disabled={suggesting || creating || seedNotes.trim().length < 8}
            >
              {suggesting ? 'Suggesting…' : 'Suggest names'}
            </button>
          </div>
          {suggestError && <p className="bs-style-choice__hint">{suggestError}</p>}
          {suggestions.length > 0 && (
            <ul className="bs-wizard-list" aria-label="Suggested names">
              {suggestions.map((s) => (
                <li key={s.name}>
                  <button
                    type="button"
                    className="bs-wizard-row bs-focus-start__pick"
                    onClick={() => applyHandle(s.name, null)}
                  >
                    <span className="bs-wizard-row__name">{s.name}</span>
                    {s.note && <span className="bs-wizard-row__mentions">{s.note}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bs-modal__actions">
          <button
            type="button"
            className="bs-btn bs-btn--primary"
            onClick={handleOpen}
            disabled={creating}
          >
            {creating ? 'Creating…' : 'Create session'}
          </button>
          <button type="button" className="bs-btn bs-btn--secondary" onClick={onClose} disabled={creating}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
